/**
 * Mwave Mobile Menu Component
 * 2-level navigation: main panel → sub-panel.
 * Sub-panels are portaled from block markup into data-subpanel-container on init.
 */

class MwaveMobileMenuManager {
  constructor() {
    this.menu = null;
    this.overlay = null;
    /** @type {HTMLElement[]} */
    this.triggers = [];
    this.closeButton = null;
    this.backButton = null;
    this.mainPanel = null;
    this.subpanelContainer = null;
    this.activeSubpanel = null;
    this.focusedElementBeforeOpen = null;
    this.previousScrollY = 0;
    this.focusTrapHandler = null;
    this.init();
  }

  init() {
    this.menu = document.querySelector('[data-mobile-menu]');
    this.overlay = document.querySelector('[data-mobile-menu-overlay]');
    this.triggers = [...document.querySelectorAll('[data-mobile-menu-trigger]')];
    this.closeButton = document.querySelector('[data-mobile-menu-close]');
    this.backButton = document.querySelector('[data-menu-back]');
    this.mainPanel = document.querySelector('[data-menu-panel="main"]');
    this.subpanelContainer = document.querySelector('[data-subpanel-container]');

    if (!this.menu || !this.overlay) return;

    this.portalSubpanels();
    this.setupEventListeners();
    this.setupKeyboard();
  }

  portalSubpanels() {
    if (!this.subpanelContainer) return;
    const subpanels = this.menu.querySelectorAll('[data-subpanel]');
    subpanels.forEach(panel => this.subpanelContainer.appendChild(panel));
  }

  setupEventListeners() {
    for (const trigger of this.triggers) {
      trigger.addEventListener('click', () => this.open());
    }

    this.closeButton?.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());
    this.backButton?.addEventListener('click', () => this.navigateBack());

    document.addEventListener('mobile-menu:toggle', () => {
      this.isOpen() ? this.close() : this.open();
    });

    document.addEventListener('mobile-menu:close', () => {
      if (this.isOpen()) this.close();
    });

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-open-subpanel]');
      if (trigger) this.openSubpanel(trigger.dataset.openSubpanel);
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-subpanel-back]')) {
        e.preventDefault();
        e.stopPropagation();
        this.closeSubpanel();
      }
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-subpanel-close]')) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    });
  }

  setupKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.isOpen()) return;
      if (this.activeSubpanel) {
        this.closeSubpanel();
      } else {
        this.close();
      }
    });
  }

  open() {
    this.focusedElementBeforeOpen = document.activeElement;
    const scrollY = window.scrollY;
    this.previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      this.menu.classList.add('is-active');
      this.overlay.classList.add('is-active');

      for (const trigger of this.triggers) {
        trigger.setAttribute('aria-expanded', 'true');
      }

      document.querySelector('[data-mobile-menu-toggle]')?.setAttribute('aria-expanded', 'true');

      this.menu.setAttribute('aria-hidden', 'false');
      this.menu.removeAttribute('inert');

      document.dispatchEvent(new CustomEvent('mobile-menu:opened'));
      this.focusFirstElement();
      this.setupFocusTrap();
    });
  }

  close() {
    this.menu.classList.remove('is-active');
    this.overlay.classList.remove('is-active');

    for (const trigger of this.triggers) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    document.querySelector('[data-mobile-menu-toggle]')?.setAttribute('aria-expanded', 'false');

    this.menu.setAttribute('aria-hidden', 'true');
    this.menu.inert = true;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.previousScrollY, behavior: 'instant' });

    if (this.activeSubpanel) this.closeSubpanel();

    this.removeFocusTrap();
    document.dispatchEvent(new CustomEvent('mobile-menu:closed'));

    if (this.focusedElementBeforeOpen) {
      this.focusedElementBeforeOpen.focus();
      this.focusedElementBeforeOpen = null;
    }
  }

  openSubpanel(panelId) {
    if (!this.subpanelContainer) return;

    const panel = this.subpanelContainer.querySelector(`[data-subpanel="${panelId}"]`);
    if (!panel) return;

    const allSubpanels = this.subpanelContainer.querySelectorAll('[data-subpanel]');
    for (const p of allSubpanels) {
      p.classList.remove('is-active');
      p.inert = true;
      p.setAttribute('aria-hidden', 'true');
    }

    panel.classList.add('is-active');
    panel.inert = false;
    panel.setAttribute('aria-hidden', 'false');

    this.activeSubpanel = panel;
    this.menu.classList.add('has-subpanel-active');

    if (this.mainPanel) this.mainPanel.inert = true;
    window.mobileBottomNav?.setInert(true);

    if (this.backButton) {
      this.backButton.style.opacity = '1';
      this.backButton.style.pointerEvents = 'auto';
    }

    const firstFocusable = panel.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) setTimeout(() => firstFocusable.focus(), 300);
  }

  closeSubpanel() {
    this.menu.classList.remove('has-subpanel-active');

    if (this.activeSubpanel) {
      this.activeSubpanel.classList.remove('is-active');
      this.activeSubpanel.inert = true;
      this.activeSubpanel.setAttribute('aria-hidden', 'true');
    }

    this.activeSubpanel = null;

    if (this.mainPanel) this.mainPanel.inert = false;
    window.mobileBottomNav?.setInert(false);

    if (this.backButton) {
      this.backButton.style.opacity = '0';
      this.backButton.style.pointerEvents = 'none';
    }
  }

  navigateBack() {
    if (!this.activeSubpanel) return;

    const panelId = this.activeSubpanel.dataset.subpanel;
    this.closeSubpanel();

    const focusTarget = this.mainPanel?.querySelector(`[data-open-subpanel="${panelId}"]`);
    if (focusTarget) setTimeout(() => focusTarget.focus(), 300);
  }

  isOpen() {
    return this.menu.classList.contains('is-active');
  }

  focusFirstElement() {
    const candidates = this.menu.querySelectorAll('button:not([disabled]), a[href]');
    for (const el of candidates) {
      if (el.closest('[inert]')) continue;
      setTimeout(() => el.focus(), 100);
      break;
    }
  }

  getFocusableElements() {
    const selectors = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const container = this.activeSubpanel || this.menu;
    return Array.from(container.querySelectorAll(selectors)).filter(el => {
      if (el.closest('[inert]')) return false;
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
    });
  }

  setupFocusTrap() {
    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab' || !this.isOpen()) return;

      const focusable = this.getFocusableElements();
      let trapElements = focusable;

      if (!this.activeSubpanel) {
        const bottomNavFocusable = window.mobileBottomNav?.getFocusableElements() ?? [];
        trapElements = [...new Set([...focusable, ...bottomNavFocusable.filter(el => !focusable.includes(el))])];
      }

      if (trapElements.length === 0) return;

      const first = trapElements[0];
      const last = trapElements[trapElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener('keydown', this.focusTrapHandler);
  }

  removeFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new MwaveMobileMenuManager());
} else {
  new MwaveMobileMenuManager();
}
